import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, query, orderBy, getDoc, setDoc, where
} from 'firebase/firestore';

const AppContext = createContext();

function compressImage(base64, maxSize = 800, quality = 0.6) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > height && width > maxSize) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      } else if (height > maxSize) {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => resolve(null);
    img.src = base64;
  });
}

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [settings, setSettings] = useState({ invoiceStartNumber: 10000 });
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('ddp_darkmode') !== 'false');

  const isAdmin = profile?.role === 'admin';
  const companyId = profile?.companyId;

  // Apply dark/light mode to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('ddp_darkmode', darkMode);
  }, [darkMode]);

  const toggleDarkMode = () => setDarkMode(d => !d);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const profileSnap = await getDoc(doc(db, 'users', u.uid));
        if (profileSnap.exists()) setProfile(profileSnap.data());
      } else {
        setProfile(null);
      }
      setAuthLoading(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!companyId) { setLogs([]); return; }
    const q = query(collection(db, 'logs'), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) { setCustomers([]); return; }
    const q = query(collection(db, 'customers'), where('companyId', '==', companyId));
    const unsub = onSnapshot(q, (snap) => {
      setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) { setPayments([]); return; }
    const q = query(collection(db, 'payments'), where('companyId', '==', companyId), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return;
    const unsub = onSnapshot(doc(db, 'settings', companyId), (snap) => {
      if (snap.exists()) setSettings(snap.data());
    });
    return unsub;
  }, [companyId]);

  const signupCompany = async (email, password, name, companyName) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    const companyId = cred.user.uid;
    await setDoc(doc(db, 'users', cred.user.uid), { role: 'admin', companyId, companyName, name, email });
    await setDoc(doc(db, 'settings', companyId), {
      invoiceStartNumber: 10000,
      companyName,
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyZip: '',
      companyPhone: '',
      companyEmail: '',
    });
    return cred.user;
  };

  const signupDriver = async (email, password, name, companyCode) => {
    const companySnap = await getDoc(doc(db, 'settings', companyCode));
    if (!companySnap.exists()) throw new Error('invalid-company');
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: name });
    await setDoc(doc(db, 'users', cred.user.uid), {
      role: 'driver', companyId: companyCode,
      companyName: companySnap.data().companyName, name, email,
    });
    return cred.user;
  };

  const login = async (email, password) => signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);

  const addLog = async (log) => {
    let photoPreview = null;
    if (log.photoPreview) photoPreview = await compressImage(log.photoPreview);
    await addDoc(collection(db, 'logs'), {
      ...log, photoPreview, companyId,
      userId: user.uid,
      driverName: log.driverName || user.displayName || user.email,
      createdAt: new Date().toISOString(),
    });
  };

  const updateLog = async (id, updates) => {
    let photoPreview = updates.photoPreview;
    if (photoPreview && photoPreview.length > 200000) photoPreview = await compressImage(photoPreview);
    await updateDoc(doc(db, 'logs', id), { ...updates, photoPreview });
  };

  const deleteLog = async (id) => deleteDoc(doc(db, 'logs', id));

  const addCustomer = async (customer) => addDoc(collection(db, 'customers'), { ...customer, companyId });
  const updateCustomer = async (id, data) => updateDoc(doc(db, 'customers', id), data);
  const deleteCustomer = async (id) => deleteDoc(doc(db, 'customers', id));

  const addPayment = async (payment) => {
    await addDoc(collection(db, 'payments'), {
      ...payment, companyId, createdAt: new Date().toISOString(),
    });
  };

  const updateSettings = async (data) => {
    await setDoc(doc(db, 'settings', companyId), { ...settings, ...data }, { merge: true });
  };

  const getInvoiceNumber = async (customerId, weekKey) => {
    const docId = companyId + '_inv_' + customerId + '_' + weekKey;
    const ref = doc(db, 'invoiceNumbers', docId);
    const snap = await getDoc(ref);
    if (snap.exists()) return snap.data().number;
    const counterRef = doc(db, 'invoiceNumbers', companyId + '_counter');
    const counterSnap = await getDoc(counterRef);
    const startNum = settings.invoiceStartNumber || 10000;
    const next = counterSnap.exists() ? counterSnap.data().value : startNum;
    await setDoc(counterRef, { value: next + 1 });
    await setDoc(ref, { number: next });
    return next;
  };

  return (
    <AppContext.Provider value={{
      user, profile, isAdmin, authLoading, logs, customers, payments, settings, companyId,
      darkMode, toggleDarkMode,
      signupCompany, signupDriver, login, logout,
      addLog, updateLog, deleteLog,
      addCustomer, updateCustomer, deleteCustomer,
      addPayment, updateSettings, getInvoiceNumber,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => useContext(AppContext);
